import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { PreciseNumber, ReplaceParams } from '@fcom/core/utils';
import { Category, FinnairAmount } from '@fcom/dapi/api/models';
import { getIconForServiceCategory, getIconForSubCategory } from '@fcom/dapi/utils';

import { CartLineItem } from '../../utils/cart-line-items.utils';
import { isAncillaryBundle, isSeatBundle } from '../../modules/ancillaries/utils/category.utils';

// Localization key for the seat line (the plain-seat main line and the seat child of a seat bundle).
const SELECTED_SEAT_TITLE_KEY = 'shoppingCart.selectedSeat';

// Fixed localization keys for the bundled-service child row of each seat bundle.
const SEAT_BUNDLE_PRODUCT_TITLE_KEYS: Partial<Record<Category, string>> = {
  [Category.SEAT_WIFI]: 'ancillaries.seatWifi.shoppingCartProduct',
  [Category.SEAT_PRIORITY]: 'ancillaries.seatPriority.shoppingCartProduct',
  [Category.SEAT_MEAL]: 'ancillaries.seatMeal.shoppingCartProduct',
  [Category.SEAT_CABIN_BAGGAGE]: 'ancillaries.seatCabinBaggage.shoppingCartProduct',
};

/**
 * One product line in the shopping cart: a single ancillary category within one bound, rendered as its own
 * row (a standalone product or plain seats) or as a bundle parent row with indented child rows (seat bundles
 * and ancillary+ancillary bundles). Purely presentational — it derives quantity, price, title and icon from
 * the line items it's given; all store/catalog resolution happens upstream and arrives as inputs.
 */
@Component({
  selector: 'fin-shopping-cart-category-line',
  standalone: false,
  templateUrl: './shopping-cart-category-line.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShoppingCartCategoryLineComponent {
  // The local line items for this category within one bound, already filtered to the selected passenger.
  items = input.required<CartLineItem[]>();
  category = input.required<Category>();
  currencyCode = input<string>();
  // The resolved service-category title, used for combos and multi-variant standalone lines.
  categoryTitle = input<string>();
  // For ancillary+ancillary bundles: the titles of the constituent standalone services (resolved per flight).
  bundleConstituentTitles = input<string[]>([]);

  protected isBundle = computed(() => isSeatBundle(this.category()) || isAncillaryBundle(this.category()));
  // A standalone category with more than one variant collapses to the category title rather than one product.
  private hasMultipleVariants = computed(
    () => new Set(this.items().map((item) => item.variant ?? item.subCategory)).size > 1
  );

  // The seat numbers of the plain-seat items, in display order, for the seat line's title or params.
  private seatNumbers = computed<string>(() =>
    this.items()
      .map((item) => item.seatNumber)
      .filter(Boolean)
      .join(', ')
  );

  // The resolved seat-type name once the cart returns (e.g. "Window seat"). Undefined while the seats are
  // still local selections (no display name yet) or when a bound mixes several seat types — both fall back
  // to the generic "Selected seat" localization key.
  private seatDisplayName = computed<string | undefined>(() => {
    const names = new Set(
      this.items()
        .map((item) => item.displayName)
        .filter(Boolean)
    );
    return names.size === 1 ? [...names][0] : undefined;
  });

  // Bundles are one instance per passenger per flight, so their count is the number of items; everything else
  // sums the item quantities.
  protected count = computed(() =>
    this.isBundle() ? this.items().length : this.items().reduce((sum, item) => sum + (item.quantity ?? 0), 0)
  );

  protected price = computed<FinnairAmount>(() => {
    const amount = this.items().reduce(
      (sum, item) => sum.add(item.unitPrice.multiply(PreciseNumber.from(item.quantity ?? 0, 0))),
      PreciseNumber.ZERO
    );
    return { amount: amount.toString(), currencyCode: this.currencyCode() };
  });

  protected icon = computed<SvgLibraryIcon>(() => {
    const category = this.category();
    if (this.isBundle()) {
      return SvgLibraryIcon.DISCOUNTED_PRICE;
    }
    if (category === Category.SEAT) {
      return getIconForServiceCategory(Category.SEAT);
    }
    return this.hasMultipleVariants()
      ? getIconForServiceCategory(category)
      : getIconForSubCategory(this.items()[0].subCategory);
  });

  // The main line's title, either as plain text (combos, standalone products, and seats whose type the cart
  // has resolved) or, for still-generic seats, undefined so the localization key below renders instead.
  protected title = computed<string | undefined>(() => {
    if (this.isBundle()) {
      return this.categoryTitle();
    }
    if (this.category() === Category.SEAT) {
      const seatType = this.seatDisplayName();
      return seatType ? `${seatType} (${this.seatNumbers()})` : undefined;
    }
    return this.hasMultipleVariants() ? this.categoryTitle() : (this.items()[0].displayName ?? this.categoryTitle());
  });

  // Seats without a resolved type fall back to the generic "Selected seat ({0})" key with the seat numbers.
  protected titleKey = computed<string | undefined>(() =>
    this.category() === Category.SEAT && !this.seatDisplayName() ? SELECTED_SEAT_TITLE_KEY : undefined
  );

  protected titleParams = computed<ReplaceParams | undefined>(() =>
    this.category() === Category.SEAT && !this.seatDisplayName() ? [this.seatNumbers()] : undefined
  );

  // The indented child rows shown under a combo line (empty for standalone products and plain seats). Each is
  // a plain title (ancillary combos) or a localization key (seat bundles), with the combo's per-passenger count.
  protected childRows = computed<
    Array<{ title?: string; titleKey?: string; titleParams?: ReplaceParams; count: number }>
  >(() => {
    const category = this.category();
    const items = this.items();
    const count = items.length;

    // Ancillary+ancillary combos borrow their child cards' copy from the constituent standalone catalog
    // services, since the combo has no child copy of its own.
    if (isAncillaryBundle(category)) {
      return this.bundleConstituentTitles().map((title) => ({ title, count }));
    }

    // Seat bundles render the chosen seats plus a fixed label for the bundled service.
    if (isSeatBundle(category)) {
      const seatSelection = {
        titleKey: SELECTED_SEAT_TITLE_KEY,
        titleParams: [items.map((item) => item.seatNumber).join(', ')] as ReplaceParams,
        count,
      };
      const serviceTitleKey = SEAT_BUNDLE_PRODUCT_TITLE_KEYS[category];
      return serviceTitleKey ? [seatSelection, { titleKey: serviceTitleKey, count }] : [];
    }

    return [];
  });
}
